import React, { memo, useCallback, useMemo } from 'react'
import { Box, Checkbox, Grid, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import { selectMultiStrikeChartStyles } from '../../../../../selectors/multiStrikeChart'
import { setMultiStrikeChartStyles } from '../../../../../reducer/multiStrikeChart.reducer'
import { LineType } from 'lightweight-charts'
import { ChartViewKey } from '../../../../../assets/constants'
import { updateMultiStrikeChartConfig } from '../../../../../reducer/chart.reducer'
import { selectMultiStrikeChartConfigStyles } from '../../../../../selectors/chart'
import AnalyticsManager from '../../../../../analytics/AnalyticsManager'
import SeriesOutputPlotTypesView from './OutputPlotTypes'
import { NotifyInterval } from '../../../../../pages/home/openInterestHeader/MultiStrikeHeader'

export const thicknessButtons = () => {
    return (
        [<ToggleButton key={1} value={1} aria-label="left aligned">
            <Box sx={{ height: '1px', backgroundColor: (theme) => theme.palette.text.primary, width: 10 }} />
        </ToggleButton>,
        <ToggleButton key={2} value={2} aria-label="centered">
            <Box sx={{ height: '2px', backgroundColor: (theme) => theme.palette.text.primary, width: 10 }} />
        </ToggleButton>,
        <ToggleButton key={3} value={3} aria-label="centered">
            <Box sx={{ height: '3px', backgroundColor: (theme) => theme.palette.text.primary, width: 10 }} />
        </ToggleButton>,
        <ToggleButton key={4} value={4} aria-label="right aligned">
            <Box sx={{ height: '4px', backgroundColor: (theme) => theme.palette.text.primary, width: 15 }} />
        </ToggleButton>]
    )
}

export const GroupOutputs = ({ chartId }: { chartId: string }) => {
    const dispatch = useDispatch()
    const isWidget = useMemo(() => ChartViewKey.MULTI_STRIKES_CHART !== chartId, [chartId])
    const {
        lineThickness,
        lineType,
        autoRefresh,
        notifyInterval
    } = useSelector(isWidget ? selectMultiStrikeChartConfigStyles(chartId) : selectMultiStrikeChartStyles)

    const handleThicknessChange = useCallback((thickness: number) => {
        AnalyticsManager.logEvent('Setting Style Changed', { source: 'multistrike', type: 'thickness' })
        if (isWidget) {
            dispatch(updateMultiStrikeChartConfig({
                viewKey: chartId,
                config: {
                    styles: {
                        lineThickness: thickness,
                        lineType,
                        autoRefresh,
                        notifyInterval
                    }
                }
            }))
        } else {
            dispatch(setMultiStrikeChartStyles({
                lineThickness: thickness,
                lineType,
                autoRefresh,
                notifyInterval
            }))
        }
    }, [chartId, dispatch, isWidget, lineType, autoRefresh, notifyInterval])

    const handleAutoRefresh = useCallback((autoRefresh: boolean) => {
        AnalyticsManager.logEvent('Setting Auto Refresh Toggled', {
            source: 'multistrike',
            type: 'autoRefresh',
            value: autoRefresh
        })
        if (isWidget) {
            dispatch(updateMultiStrikeChartConfig({
                viewKey: chartId,
                config: {
                    styles: {
                        autoRefresh: autoRefresh,
                        lineType,
                        lineThickness,
                        notifyInterval
                    }
                }
            }))
        } else {
            dispatch(setMultiStrikeChartStyles({
                autoRefresh: autoRefresh,
                lineType,
                lineThickness,
                notifyInterval
            }))
        }
    }, [chartId, dispatch, isWidget, lineType, lineThickness, notifyInterval])

    const handlePlotTypeChange = useCallback((plotType: LineType) => {
        if (isWidget) {
            dispatch(updateMultiStrikeChartConfig({
                viewKey: chartId,
                config: {
                    styles: {
                        lineThickness,
                        lineType: plotType,
                        autoRefresh,
                        notifyInterval
                    }
                }
            }))
        } else {
            dispatch(setMultiStrikeChartStyles({
                lineThickness,
                lineType: plotType,
                autoRefresh,
                notifyInterval
            }))
        }
    }, [chartId, dispatch, isWidget, lineThickness, autoRefresh, notifyInterval])

    const handleNotifyIntervalChange = useCallback((interval: number) => {
        AnalyticsManager.logEvent('Notify Clicked', { source: 'settings', timeInterval: interval })
        if (isWidget) {
            dispatch(updateMultiStrikeChartConfig({
                viewKey: chartId,
                config: {
                    styles: {
                        lineThickness,
                        lineType,
                        autoRefresh,
                        notifyInterval: interval
                    }
                }
            }))
        } else {
            dispatch(setMultiStrikeChartStyles({
                lineThickness,
                lineType,
                autoRefresh,
                notifyInterval: interval
            }))
        }
    }, [chartId, dispatch, isWidget, lineThickness, autoRefresh, lineType])

    return (
        <Grid container gap={1}>
            <>
                <Grid container key={'thickness'} sx={{ my: 0.5 }}>
                    <Grid item xs={4}><Typography color="inherit"
                        sx={{ flexBasis: 2 }}>{'Thickness'}</Typography></Grid>
                    <Grid item xs={8}>
                        <ToggleButtonGroup size="small" value={lineThickness} exclusive
                            onChange={(e, val) => handleThicknessChange(val)} orientation="horizontal"
                            sx={{ height: 24 }}>
                            {thicknessButtons()}
                        </ToggleButtonGroup>
                    </Grid>
                </Grid>
                <Grid container key={'Plot type'} sx={{ my: 0.5 }}>
                    <Grid item xs={4}><Typography color="inherit"
                        sx={{ flexBasis: 2 }}>{'Plot type'}</Typography></Grid>
                    <Grid item xs={8}>
                        <SeriesOutputPlotTypesView lineType={lineType} onSelected={handlePlotTypeChange} />
                    </Grid>
                </Grid>
                <Typography my={0.5} color="textSecondary" variant={'caption'}>ATM Update</Typography>
                <Grid container key="autoRefresh" sx={{ alignItems: 'center' }}>
                    <Grid item xs={4}>
                        <Typography color="inherit" sx={{ flexBasis: 2 }}>
                            Auto
                        </Typography>
                    </Grid>
                    <Grid item xs={8}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Checkbox
                                sx={{
                                    padding: 0
                                }}
                                checked={autoRefresh}
                                onChange={(e, checked) => {
                                    handleAutoRefresh(checked)
                                }}
                            />
                            <Typography variant="caption" color="textSecondary" sx={{ ml: 1 }}>
                                (ATM will update every 30 sec)
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>
                <Grid container key={'Notify'} sx={{ my: 0.5 }}>
                    <Grid item xs={4}><Typography color="inherit"
                        sx={{ flexBasis: 2 }}>{'Notify'}</Typography></Grid>
                    <Grid item xs={8}>
                        <Stack spacing={1}>
                            <ToggleButtonGroup
                                disabled={autoRefresh}
                                size="small"
                                value={notifyInterval}
                                exclusive
                                onChange={(e, val) => handleNotifyIntervalChange(val)}
                                orientation="horizontal"
                                sx={{ height: '24px' }}
                                aria-label="Notification Interval"
                            >
                                {NotifyInterval.map((interval) => (
                                    <ToggleButton key={interval.value} value={interval.value} aria-label={interval.label}>
                                        {interval.label}
                                    </ToggleButton>
                                ))}
                            </ToggleButtonGroup>
                            <Typography variant="caption" color="textSecondary" sx={{ ml: 1 }}>
                                (Get notified if ATM changes)
                            </Typography>
                        </Stack>
                    </Grid>
                </Grid>
            </>
        </Grid>
    )
}

export default memo(GroupOutputs)


